import { extractErrorMessage } from '../lib/api-errors';

/**
 * The slice of store state that `withMutation` reads and writes.
 * Narrowing `set` to this shape (rather than `Record<string, unknown>`)
 * ensures the helper can only touch keys it actually owns, so typos like
 * `{ isMuttating: true }` are caught at compile time.
 */
type MutationState = { isMutating: boolean; error: string | null };

/**
 * A minimal Zustand-compatible `set` signature that covers the partial-state
 * overload used by mutations in this project. The full Zustand `set` type
 * accepts additional overloads (state updater function, replace flag) that
 * mutations never need; accepting a narrower type keeps the helper generic
 * without depending on Zustand internals.
 */
type PartialSetFn = (partial: Partial<MutationState>) => void;

/**
 * Shared boilerplate for store mutations that follow the pattern:
 *
 *   1. set({ isMutating: true, error: null })
 *   2. await apiCall()  — on error: set error + isMutating:false, rethrow
 *   3. (caller handles success — the helper does NOT reset isMutating)
 *
 * The helper intentionally does NOT reset `isMutating` on success: each
 * mutation's success path includes at least one additional `set()` call
 * (e.g. updating `selectedTrade`, then refreshing the list), and it is
 * cleaner for the caller to include `isMutating: false` in that call rather
 * than having the helper clear it prematurely or duplicate it via `finally`.
 *
 * On error the helper sets `{ error, isMutating: false }` and rethrows so
 * the original call-site exception propagates to the caller (matching the
 * previous `throw error` in each catch block).
 *
 * @param set       Zustand `set` — partial-state overload only
 * @param fn        Async API call to execute
 * @param errorMsg  Fallback message passed to `extractErrorMessage`
 * @returns         Resolved value of `fn`
 * @throws          Re-throws the caught error after updating store state
 */
export async function withMutation<T>(
  set: PartialSetFn,
  fn: () => Promise<T>,
  errorMsg: string,
): Promise<T> {
  set({ isMutating: true, error: null });
  try {
    return await fn();
  } catch (error) {
    set({ error: extractErrorMessage(error, errorMsg), isMutating: false });
    throw error;
  }
}
